import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save';
import CodeIcon from '@mui/icons-material/Code';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  terraformApi,
  ProviderSchemaVersion,
  TerraformResourceSchema,
  TerraformBlockSchema,
  ModuleSummary,
  ModuleVariable,
  ModuleRegistryConfig,
  TerraformTemplate,
  TerraformTemplateSummary,
  TemplateVariable,
  OptionsSource,
} from '../services/terraform-api';
import { gitlabApi, GitLabInstance, GitLabProject } from '../services/gitlab-api';
import { TerraformFormRenderer } from './TerraformFormRenderer';
import { HCLPreviewPanel } from './HCLPreviewPanel';
import { TerraformResourcePicker } from './TerraformResourcePicker';
import { TerraformModulePicker } from './TerraformModulePicker';
import { TerraformCommitDialog } from './TerraformCommitDialog';
import { TerraformTemplateEditor } from './TerraformTemplateEditor';

// ============================================================================
// Types
// ============================================================================

interface ConfigItem {
  id: string;
  type: 'resource' | 'module';
  resourceType?: string;
  moduleName?: string;
  moduleSource?: string;
  instanceName: string;
  values: Record<string, any>;
  schema?: TerraformBlockSchema;
  moduleVariables?: ModuleVariable[];
  hcl: string;
}

// ============================================================================
// Schema Import Dialog
// ============================================================================

const SchemaImportDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onImport: () => void;
}> = ({ open, onClose, onImport }) => {
  const [importMode, setImportMode] = useState<'registry' | 'url' | 'file' | 'paste'>('registry');

  // Registry state
  const [registryHost, setRegistryHost] = useState('rangeos.cicd.pcte.dev/bylight/rangeos');
  const [registryProviders, setRegistryProviders] = useState<Array<{ namespace: string; type: string; source: string }>>([]);
  const [selectedProvider, setSelectedProvider] = useState<{ namespace: string; type: string; source: string } | null>(null);
  const [registryVersions, setRegistryVersions] = useState<Array<{ version: string }>>([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // URL/File/Paste state
  const [url, setUrl] = useState('');
  const [jsonContent, setJsonContent] = useState('');
  const [version, setVersion] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setJsonContent(reader.result as string);
    };
    reader.readAsText(file);
  };

  // Registry: discover providers
  const handleDiscover = async () => {
    if (!registryHost.trim()) return;
    setDiscovering(true);
    setError(null);
    setRegistryProviders([]);
    setRegistryVersions([]);
    setSelectedProvider(null);
    setSelectedVersion('');

    try {
      const providers = await terraformApi.discoverProviders(registryHost.trim());
      setRegistryProviders(providers);
      if (providers.length === 1) {
        setSelectedProvider(providers[0]);
        // Auto-load versions
        await handleLoadVersions(providers[0]);
      } else if (providers.length === 0) {
        setError('No providers found at this registry. Try specifying the namespace in the registry host or check the URL.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDiscovering(false);
    }
  };

  // Registry: load versions for a provider
  const handleLoadVersions = async (provider: { namespace: string; type: string }) => {
    setLoadingVersions(true);
    setError(null);
    try {
      const versions = await terraformApi.listRegistryVersions(
        registryHost.trim(),
        provider.namespace,
        provider.type,
      );
      setRegistryVersions(versions);
      if (versions.length > 0) {
        setSelectedVersion(versions[0].version);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Import handler
  const handleImport = async () => {
    setLoading(true);
    setError(null);
    setStatusMessage(null);

    try {
      if (importMode === 'registry') {
        if (!selectedProvider || !selectedVersion) {
          setError('Select a provider and version');
          setLoading(false);
          return;
        }
        setStatusMessage('Running terraform init and extracting schema... This may take a minute.');
        await terraformApi.extractFromRegistry(selectedProvider.source, selectedVersion);
      } else if (importMode === 'url') {
        if (!url.trim()) { setError('Please provide a URL'); setLoading(false); return; }
        await terraformApi.importSchemaFromUrl(url.trim(), version || undefined);
      } else {
        if (!jsonContent.trim()) { setError('Please provide schema JSON'); setLoading(false); return; }
        const parsed = JSON.parse(jsonContent);
        await terraformApi.importSchema(parsed, version || undefined);
      }
      onImport();
      onClose();
      setUrl('');
      setJsonContent('');
      setVersion('');
      setStatusMessage(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
      setStatusMessage(null);
    } finally {
      setLoading(false);
    }
  };

  const canImport =
    importMode === 'registry'
      ? !!(selectedProvider && selectedVersion)
      : importMode === 'url'
        ? !!url.trim()
        : !!jsonContent.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Provider Schema</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {statusMessage && <Alert severity="info" sx={{ mb: 2 }}>{statusMessage}</Alert>}

        <Tabs
          value={importMode}
          onChange={(_, v) => setImportMode(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="From Registry" value="registry" />
          <Tab label="From URL" value="url" />
          <Tab label="Upload File" value="file" />
          <Tab label="Paste JSON" value="paste" />
        </Tabs>

        {importMode === 'registry' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Connect to a Terraform provider registry to discover providers and versions automatically.
            </Typography>

            {/* Registry host + discover */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                label="Provider Source"
                value={registryHost}
                onChange={e => setRegistryHost(e.target.value)}
                placeholder="rangeos.cicd.pcte.dev/bylight/rangeos"
                helperText="registry-host/namespace/type"
                sx={{ flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') handleDiscover(); }}
              />
              <Button
                variant="outlined"
                onClick={handleDiscover}
                disabled={discovering || !registryHost.trim()}
                startIcon={discovering ? <CircularProgress size={16} /> : <RefreshIcon />}
                sx={{ minWidth: 120, height: 40 }}
              >
                {discovering ? 'Searching...' : 'Discover'}
              </Button>
            </Box>

            {/* Provider selection */}
            {registryProviders.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={selectedProvider?.source || ''}
                  label="Provider"
                  onChange={e => {
                    const p = registryProviders.find(p => p.source === e.target.value);
                    if (p) {
                      setSelectedProvider(p);
                      handleLoadVersions(p);
                    }
                  }}
                >
                  {registryProviders.map(p => (
                    <MenuItem key={p.source} value={p.source}>
                      {p.source} ({p.namespace}/{p.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedProvider && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={selectedProvider.source} color="primary" size="small" />
                {loadingVersions && <CircularProgress size={16} />}
              </Box>
            )}

            {/* Version selection */}
            {registryVersions.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel>Version</InputLabel>
                <Select
                  value={selectedVersion}
                  label="Version"
                  onChange={e => setSelectedVersion(e.target.value)}
                >
                  {registryVersions.map(v => (
                    <MenuItem key={v.version} value={v.version}>
                      {v.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedProvider && selectedVersion && (
              <Alert severity="info" variant="outlined">
                Will run <code>terraform init</code> + <code>terraform providers schema -json</code> for{' '}
                <strong>{selectedProvider.source}@{selectedVersion}</strong>.
                Requires <code>terraform</code> CLI on the server.
              </Alert>
            )}
          </Box>
        )}

        {importMode === 'url' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Enter the URL to a <code>terraform providers schema -json</code> output file.
            </Typography>
            <TextField
              size="small"
              label="Version Label (optional)"
              value={version}
              onChange={e => setVersion(e.target.value)}
              placeholder="e.g. 1.2.3"
              sx={{ width: 200, mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Schema URL"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://gitlab.example.com/group/repo/-/raw/main/schema.json"
              autoFocus
            />
          </Box>
        )}

        {importMode === 'file' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Upload a JSON file from <code>terraform providers schema -json</code>.
            </Typography>
            <TextField
              size="small"
              label="Version Label (optional)"
              value={version}
              onChange={e => setVersion(e.target.value)}
              placeholder="e.g. 1.2.3"
              sx={{ width: 200, mb: 2 }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 1 }}
            >
              Choose File
            </Button>
            {jsonContent && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {(jsonContent.length / 1024).toFixed(0)} KB loaded
              </Typography>
            )}
          </Box>
        )}

        {importMode === 'paste' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Paste the output of <code>terraform providers schema -json</code>.
            </Typography>
            <TextField
              size="small"
              label="Version Label (optional)"
              value={version}
              onChange={e => setVersion(e.target.value)}
              placeholder="e.g. 1.2.3"
              sx={{ width: 200, mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={12}
              value={jsonContent}
              onChange={e => setJsonContent(e.target.value)}
              placeholder="Paste terraform providers schema -json output here..."
              sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || !canImport}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? (importMode === 'registry' ? 'Extracting...' : 'Importing...') : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Module Registry Config Dialog
// ============================================================================

const ModuleConfigDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  currentConfig: ModuleRegistryConfig | null;
}> = ({ open, onClose, onSave, currentConfig }) => {
  const [sourceType, setSourceType] = useState<'url' | 'gitlab'>(
    currentConfig?.type === 'gitlab' ? 'gitlab' : 'url',
  );

  // URL state
  const [moduleUrl, setModuleUrl] = useState(
    currentConfig?.type === 'url' ? currentConfig.url : '',
  );

  // GitLab state
  const [instances, setInstances] = useState<GitLabInstance[]>([]);
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [gitlabInstanceId, setGitlabInstanceId] = useState(
    currentConfig?.type === 'gitlab' ? currentConfig.gitlabInstanceId : '',
  );
  const [projectId, setProjectId] = useState<number | ''>(
    currentConfig?.type === 'gitlab' ? currentConfig.projectId : '',
  );
  const [modulesPath, setModulesPath] = useState(
    currentConfig?.type === 'gitlab' ? currentConfig.modulesPath : 'modules',
  );
  const [branch, setBranch] = useState(
    currentConfig?.type === 'gitlab' ? currentConfig.branch : 'main',
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      if (currentConfig?.type === 'gitlab') {
        setSourceType('gitlab');
        setGitlabInstanceId(currentConfig.gitlabInstanceId);
        setProjectId(currentConfig.projectId);
        setModulesPath(currentConfig.modulesPath);
        setBranch(currentConfig.branch);
        loadInstances();
      } else if (currentConfig?.type === 'url') {
        setSourceType('url');
        setModuleUrl(currentConfig.url);
      }
    }
  }, [open, currentConfig]);

  useEffect(() => {
    if (sourceType === 'gitlab' && instances.length === 0) loadInstances();
  }, [sourceType]);

  useEffect(() => {
    if (gitlabInstanceId) loadProjects();
  }, [gitlabInstanceId]);

  const loadInstances = async () => {
    try {
      const data = await gitlabApi.listInstances();
      setInstances(data);
    } catch {}
  };

  const loadProjects = async () => {
    try {
      const data = await gitlabApi.listProjects(gitlabInstanceId);
      setProjects(data);
    } catch {}
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (sourceType === 'url') {
        if (!moduleUrl.trim()) { setError('URL is required'); setLoading(false); return; }
        await terraformApi.setModuleConfig({ type: 'url', url: moduleUrl.trim() });
      } else {
        if (!gitlabInstanceId || !projectId || !modulesPath || !branch) {
          setError('All fields are required');
          setLoading(false);
          return;
        }
        await terraformApi.setModuleConfig({
          type: 'gitlab',
          gitlabInstanceId,
          projectId: projectId as number,
          modulesPath,
          branch,
        });
      }
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Module Registry Configuration</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs
          value={sourceType}
          onChange={(_, v) => setSourceType(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="Archive URL" value="url" />
          <Tab label="GitLab Repo" value="gitlab" />
        </Tabs>

        {sourceType === 'url' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Point to a <code>.tar.gz</code> archive containing module directories.
              Use <code>//</code> to specify the subdirectory within the archive.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Module Archive URL"
              value={moduleUrl}
              onChange={e => setModuleUrl(e.target.value)}
              placeholder="https://modules.cicd.pcte.dev/develop_modules.tar.gz//modules/"
              helperText="Use // to separate archive URL from subdirectory path"
            />
          </Box>
        )}

        {sourceType === 'gitlab' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>GitLab Instance</InputLabel>
              <Select
                value={gitlabInstanceId}
                label="GitLab Instance"
                onChange={e => { setGitlabInstanceId(e.target.value); setProjectId(''); }}
              >
                {instances.map(inst => (
                  <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Project (terraform-modules repo)</InputLabel>
              <Select
                value={projectId}
                label="Project (terraform-modules repo)"
                onChange={e => setProjectId(e.target.value as number)}
                disabled={!gitlabInstanceId}
              >
                {projects.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name_with_namespace}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Modules Path"
              value={modulesPath}
              onChange={e => setModulesPath(e.target.value)}
              helperText="Path within the repo where modules are stored"
            />

            <TextField
              fullWidth
              size="small"
              label="Branch"
              value={branch}
              onChange={e => setBranch(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Main TerraformBuilder Component
// ============================================================================

export const TerraformBuilder: React.FC = () => {
  // Schema state
  const [versions, setVersions] = useState<ProviderSchemaVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [resourceTypes, setResourceTypes] = useState<string[]>([]);

  // Module state
  const [modules, setModules] = useState<ModuleSummary[]>([]);
  const [moduleConfig, setModuleConfig] = useState<ModuleRegistryConfig | null>(null);

  // Template state
  const [templates, setTemplates] = useState<TerraformTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<TerraformTemplate | null>(null);
  const [templateValues, setTemplateValues] = useState<Record<string, any>>({});
  const [templateHclPreview, setTemplateHclPreview] = useState('');
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TerraformTemplate | null>(null);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [dynamicOptionsLoading, setDynamicOptionsLoading] = useState<Record<string, boolean>>({});

  // Config items (resources + modules being configured)
  const [configItems, setConfigItems] = useState<ConfigItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // UI state
  const [mode, setMode] = useState<'resources' | 'modules' | 'templates'>('resources');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [showModulePicker, setShowModulePicker] = useState(false);
  const [showModuleConfig, setShowModuleConfig] = useState(false);
  const [showCommitDialog, setShowCommitDialog] = useState(false);

  const selectedItem = configItems.find(i => i.id === selectedItemId) || null;

  // ============================================================================
  // Data Loading
  // ============================================================================

  const loadVersions = useCallback(async () => {
    try {
      const data = await terraformApi.listVersions();
      setVersions(data);
      if (data.length > 0 && !selectedVersion) {
        setSelectedVersion(data[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load schema versions:', err);
    }
  }, [selectedVersion]);

  const loadResourceTypes = useCallback(async () => {
    if (!selectedVersion) return;
    try {
      const types = await terraformApi.listResourceTypes(selectedVersion);
      setResourceTypes(types);
    } catch (err: any) {
      console.error('Failed to load resource types:', err);
    }
  }, [selectedVersion]);

  const loadModules = useCallback(async () => {
    try {
      const [config, mods] = await Promise.all([
        terraformApi.getModuleConfig(),
        terraformApi.listModules().catch(() => []),
      ]);
      setModuleConfig(config);
      setModules(mods);
    } catch (err: any) {
      console.error('Failed to load modules:', err);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await terraformApi.listTemplates();
      setTemplates(data);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
    }
  }, []);

  const selectTemplate = useCallback(async (id: string) => {
    setSelectedTemplateId(id);
    setDynamicOptions({});
    setDynamicOptionsLoading({});
    try {
      const tpl = await terraformApi.getTemplate(id);
      setActiveTemplate(tpl);
      // Initialize values with defaults
      const defaults: Record<string, any> = {};
      for (const v of tpl.variables) {
        if (v.default !== undefined) defaults[v.name] = v.default;
      }
      setTemplateValues(defaults);
      // Render initial preview
      const hcl = await terraformApi.renderTemplate(id, defaults);
      setTemplateHclPreview(hcl);
      // Fetch dynamic options for variables that have optionsSource
      for (const v of tpl.variables) {
        if (v.optionsSource) {
          setDynamicOptionsLoading(prev => ({ ...prev, [v.name]: true }));
          terraformApi.resolveOptions(v.optionsSource).then(result => {
            setDynamicOptions(prev => ({ ...prev, [v.name]: result.options }));
          }).catch(() => {}).finally(() => {
            setDynamicOptionsLoading(prev => ({ ...prev, [v.name]: false }));
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to load template:', err);
    }
  }, []);

  const updateTemplateValue = useCallback(async (key: string, value: any) => {
    setTemplateValues(prev => {
      const next = { ...prev, [key]: value };
      // Fire off render in background
      if (selectedTemplateId) {
        terraformApi.renderTemplate(selectedTemplateId, next).then(hcl => {
          setTemplateHclPreview(hcl);
        }).catch(() => {});
      }
      return next;
    });
  }, [selectedTemplateId]);

  useEffect(() => { loadVersions(); loadModules(); loadTemplates(); }, []);
  useEffect(() => { loadResourceTypes(); }, [selectedVersion]);

  // ============================================================================
  // HCL Generation
  // ============================================================================

  const regenerateHCL = useCallback(async (item: ConfigItem): Promise<string> => {
    try {
      if (item.type === 'resource') {
        return await terraformApi.generateResourceHCL(
          item.resourceType!,
          item.instanceName,
          item.values,
          selectedVersion || undefined,
        );
      } else {
        return await terraformApi.generateModuleHCL(
          item.moduleName!,
          item.moduleSource!,
          item.instanceName,
          item.values,
        );
      }
    } catch {
      return '# Error generating HCL';
    }
  }, [selectedVersion]);

  const updateItemValues = useCallback(async (itemId: string, values: Record<string, any>) => {
    setConfigItems(prev => {
      const idx = prev.findIndex(i => i.id === itemId);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], values };
      return updated;
    });

    // Debounced HCL regeneration
    const item = configItems.find(i => i.id === itemId);
    if (item) {
      const hcl = await regenerateHCL({ ...item, values });
      setConfigItems(prev => {
        const idx = prev.findIndex(i => i.id === itemId);
        if (idx < 0) return prev;
        const updated = [...prev];
        updated[idx] = { ...updated[idx], values, hcl };
        return updated;
      });
    }
  }, [configItems, regenerateHCL]);

  // ============================================================================
  // Add Resource / Module
  // ============================================================================

  const addResource = useCallback(async (resourceType: string) => {
    const namePrompt = resourceType.replace(/^[^_]+_/, ''); // strip provider prefix
    const instanceName = `${namePrompt}_1`;

    let schema: TerraformBlockSchema | undefined;
    try {
      const resourceSchema = await terraformApi.getResourceSchema(selectedVersion, resourceType);
      schema = resourceSchema.block;
    } catch {}

    const newItem: ConfigItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'resource',
      resourceType,
      instanceName,
      values: {},
      schema,
      hcl: `resource "${resourceType}" "${instanceName}" {\n}`,
    };

    setConfigItems(prev => [...prev, newItem]);
    setSelectedItemId(newItem.id);
  }, [selectedVersion]);

  const addModule = useCallback(async (moduleName: string) => {
    const instanceName = moduleName.replace(/-/g, '_');

    let moduleVariables: ModuleVariable[] = [];
    try {
      moduleVariables = await terraformApi.getModuleVariables(moduleName);
    } catch {}

    const moduleSource = moduleConfig
      ? `git::https://gitlab.example.com/${moduleName}.git//modules/${moduleName}`
      : moduleName;

    const newItem: ConfigItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'module',
      moduleName,
      moduleSource,
      instanceName,
      values: {},
      moduleVariables,
      hcl: `module "${instanceName}" {\n  source = "${moduleSource}"\n}`,
    };

    setConfigItems(prev => [...prev, newItem]);
    setSelectedItemId(newItem.id);
  }, [moduleConfig]);

  const removeItem = useCallback((itemId: string) => {
    setConfigItems(prev => prev.filter(i => i.id !== itemId));
    if (selectedItemId === itemId) {
      setSelectedItemId(null);
    }
  }, [selectedItemId]);

  // ============================================================================
  // Combined HCL
  // ============================================================================

  const combinedHCL = configItems.map(i => i.hcl).filter(Boolean).join('\n\n');

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 140px)' }}>
      {/* Left Sidebar */}
      <Paper
        sx={{
          width: 300,
          minWidth: 300,
          display: 'flex',
          flexDirection: 'column',
          borderRight: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
        square
        elevation={0}
      >
        {/* Top controls */}
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          {/* Version selector */}
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Provider Version</InputLabel>
            <Select
              value={selectedVersion}
              label="Provider Version"
              onChange={e => setSelectedVersion(e.target.value)}
            >
              {versions.map(v => (
                <MenuItem key={v.id} value={v.id}>
                  {v.providerSource.split('/').pop()} v{v.version} ({v.resourceCount} resources)
                </MenuItem>
              ))}
              {versions.length === 0 && (
                <MenuItem disabled>No schemas imported</MenuItem>
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" startIcon={<UploadIcon />} onClick={() => setShowImportDialog(true)}>
              Import
            </Button>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => { if (v) setMode(v); }}
              size="small"
            >
              <ToggleButton value="resources">
                <Tooltip title="Resources"><CodeIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="modules">
                <Tooltip title="Modules"><ViewModuleIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="templates">
                <Tooltip title="Templates"><DescriptionIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            {mode === 'modules' && (
              <Tooltip title="Module Registry Config">
                <IconButton size="small" onClick={() => setShowModuleConfig(true)}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Add button */}
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          {mode === 'templates' ? (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => { setEditingTemplate(null); setShowTemplateEditor(true); }}
              >
                New Template
              </Button>
              <Button
                fullWidth
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => { setEditingTemplate(null); setShowTemplateEditor(true); }}
                sx={{ mt: 0.5 }}
              >
                Import from GitLab
              </Button>
            </>
          ) : (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => mode === 'resources' ? setShowResourcePicker(true) : setShowModulePicker(true)}
                disabled={mode === 'resources' && !selectedVersion}
              >
                Add {mode === 'resources' ? 'Resource' : 'Module'}
              </Button>
              {mode === 'modules' && (
                <Button
                  fullWidth
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={async () => {
                    try {
                      await terraformApi.refreshModules();
                      await loadModules();
                    } catch {}
                  }}
                  sx={{ mt: 0.5 }}
                >
                  Refresh Modules
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Config items / Templates list */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {mode === 'templates' ? (
            <List dense disablePadding>
              {templates.map(tpl => (
                <ListItem
                  key={tpl.id}
                  disablePadding
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={async () => {
                          const full = await terraformApi.getTemplate(tpl.id);
                          setEditingTemplate(full);
                          setShowTemplateEditor(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={async () => {
                          try {
                            await terraformApi.deleteTemplate(tpl.id);
                            loadTemplates();
                            if (selectedTemplateId === tpl.id) {
                              setSelectedTemplateId(null);
                              setActiveTemplate(null);
                              setTemplateHclPreview('');
                            }
                          } catch {}
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemButton
                    selected={selectedTemplateId === tpl.id}
                    onClick={() => selectTemplate(tpl.id)}
                  >
                    <ListItemText
                      primary={tpl.name}
                      secondary={`${tpl.variables.length} variable${tpl.variables.length !== 1 ? 's' : ''}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: selectedTemplateId === tpl.id ? 600 : 400 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {templates.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates yet. Create one or import from GitLab.
                  </Typography>
                </Box>
              )}
            </List>
          ) : (
            <List dense disablePadding>
              {configItems.map(item => (
                <ListItem
                  key={item.id}
                  disablePadding
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => removeItem(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    selected={selectedItemId === item.id}
                    onClick={() => setSelectedItemId(item.id)}
                  >
                    <ListItemText
                      primary={item.instanceName}
                      secondary={item.type === 'resource' ? item.resourceType : item.moduleName}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: selectedItemId === item.id ? 600 : 400 }}
                      secondaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {configItems.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No items yet. Add a {mode === 'resources' ? 'resource' : 'module'} to get started.
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Box>

        {/* Commit button */}
        {(mode === 'templates' ? !!templateHclPreview : configItems.length > 0) && (
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => setShowCommitDialog(true)}
            >
              Commit to GitLab
            </Button>
          </Box>
        )}
      </Paper>

      {/* Right Panel */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mode === 'templates' && activeTemplate ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Template header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label="template" color="info" />
              <Typography variant="subtitle1" fontWeight={600}>
                {activeTemplate.name}
              </Typography>
              {activeTemplate.description && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {activeTemplate.description}
                </Typography>
              )}
            </Box>

            {/* Template content - form + preview split */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* Template form */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Variables
                </Typography>
                {activeTemplate.variables.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    This template has no variables.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {activeTemplate.variables.filter(v => !v.generatedFrom).map(v => {
                      // Determine dropdown options: dynamic options take priority, fall back to static
                      const dynOpts = dynamicOptions[v.name];
                      const hasDynamicSource = !!v.optionsSource;
                      const isDynLoading = dynamicOptionsLoading[v.name];
                      const effectiveOptions = dynOpts?.length ? dynOpts : (v.options?.length ? v.options.map(o => ({ label: o, value: o })) : null);

                      return (
                      <Box key={v.name}>
                        {isDynLoading ? (
                          <FormControl fullWidth size="small">
                            <InputLabel>{v.label}{v.required ? ' *' : ''}</InputLabel>
                            <Select
                              value=""
                              label={`${v.label}${v.required ? ' *' : ''}`}
                              disabled
                            >
                              <MenuItem value=""><em>Loading options...</em></MenuItem>
                            </Select>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 1.5 }}>
                              <CircularProgress size={12} />
                              <Typography variant="caption" color="text.secondary">Fetching from {v.optionsSource?.type === 'rangeos' ? 'RangeOS' : v.optionsSource?.type === 'rangeos-swagger' ? 'RangeOS Swagger' : 'provider schema'}...</Typography>
                            </Box>
                          </FormControl>
                        ) : effectiveOptions ? (
                          <FormControl fullWidth size="small">
                            <InputLabel>{v.label}{v.required ? ' *' : ''}</InputLabel>
                            <Select
                              value={templateValues[v.name] ?? v.default ?? ''}
                              label={`${v.label}${v.required ? ' *' : ''}`}
                              onChange={e => updateTemplateValue(v.name, e.target.value)}
                            >
                              {effectiveOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label === opt.value ? opt.label : `${opt.label} (${opt.value})`}
                                </MenuItem>
                              ))}
                            </Select>
                            {(v.description || hasDynamicSource) && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                                {v.description}{hasDynamicSource && dynOpts?.length ? ` — ${dynOpts.length} options from ${v.optionsSource!.type}` : ''}
                              </Typography>
                            )}
                          </FormControl>
                        ) : v.type === 'bool' ? (
                          <FormControl fullWidth size="small">
                            <InputLabel>{v.label}{v.required ? ' *' : ''}</InputLabel>
                            <Select
                              value={templateValues[v.name] ?? v.default ?? ''}
                              label={`${v.label}${v.required ? ' *' : ''}`}
                              onChange={e => updateTemplateValue(v.name, e.target.value)}
                            >
                              <MenuItem value="true">true</MenuItem>
                              <MenuItem value="false">false</MenuItem>
                            </Select>
                          </FormControl>
                        ) : v.type === 'hcl' || v.type === 'list' || v.type === 'map' ? (
                          <TextField
                            fullWidth
                            size="small"
                            label={`${v.label}${v.required ? ' *' : ''}`}
                            value={templateValues[v.name] ?? v.default ?? ''}
                            onChange={e => updateTemplateValue(v.name, e.target.value)}
                            multiline
                            rows={3}
                            helperText={v.description || `Type: ${v.type}`}
                            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            label={`${v.label}${v.required ? ' *' : ''}`}
                            value={templateValues[v.name] ?? v.default ?? ''}
                            onChange={e => updateTemplateValue(v.name, v.type === 'number' ? e.target.value : e.target.value)}
                            type={v.type === 'number' ? 'number' : 'text'}
                            helperText={v.description}
                          />
                        )}
                      </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* HCL Preview */}
              <Box
                sx={{
                  width: '45%',
                  minWidth: 300,
                  borderLeft: 1,
                  borderColor: 'divider',
                  overflow: 'auto',
                  p: 1,
                }}
              >
                <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                  HCL Preview
                </Typography>
                <HCLPreviewPanel hcl={templateHclPreview} />
              </Box>
            </Box>
          </Box>
        ) : mode === 'templates' ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
              <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Templates
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create templates from existing .tf files. Replace values with {'{{placeholders}}'} to build reusable forms.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setEditingTemplate(null); setShowTemplateEditor(true); }}
              >
                Create Template
              </Button>
            </Box>
          </Box>
        ) : selectedItem ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Item header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                label={selectedItem.type}
                color={selectedItem.type === 'resource' ? 'primary' : 'secondary'}
              />
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedItem.type === 'resource' ? selectedItem.resourceType : selectedItem.moduleName}
              </Typography>
              <TextField
                size="small"
                value={selectedItem.instanceName}
                onChange={e => {
                  const newName = e.target.value;
                  setConfigItems(prev =>
                    prev.map(i => i.id === selectedItem.id ? { ...i, instanceName: newName } : i),
                  );
                }}
                label="Name"
                sx={{ ml: 'auto', width: 200 }}
              />
            </Box>

            {/* Content area - form + preview split */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* Form */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <TerraformFormRenderer
                  schema={selectedItem.schema}
                  moduleVariables={selectedItem.moduleVariables}
                  values={selectedItem.values}
                  onChange={values => updateItemValues(selectedItem.id, values)}
                />
              </Box>

              {/* HCL Preview */}
              <Box
                sx={{
                  width: '45%',
                  minWidth: 300,
                  borderLeft: 1,
                  borderColor: 'divider',
                  overflow: 'auto',
                  p: 1,
                }}
              >
                <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                  HCL Preview
                </Typography>
                <HCLPreviewPanel hcl={selectedItem.hcl} />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Show combined HCL when no item selected */}
            {configItems.length > 0 ? (
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Combined Configuration ({configItems.length} item{configItems.length !== 1 ? 's' : ''})
                </Typography>
                <HCLPreviewPanel hcl={combinedHCL} />
              </Box>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
                  <CodeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Terraform Builder
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Build Terraform configurations using a form-based UI.
                    Import a provider schema to get started, then add resources or modules.
                  </Typography>
                  {versions.length === 0 ? (
                    <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setShowImportDialog(true)}>
                      Import Provider Schema
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowResourcePicker(true)}>
                        Add Resource
                      </Button>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setMode('modules'); setShowModulePicker(true); }}>
                        Add Module
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Dialogs */}
      <SchemaImportDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={() => { loadVersions(); }}
      />

      <TerraformResourcePicker
        open={showResourcePicker}
        onClose={() => setShowResourcePicker(false)}
        onSelect={addResource}
        resourceTypes={resourceTypes}
      />

      <TerraformModulePicker
        open={showModulePicker}
        onClose={() => setShowModulePicker(false)}
        onSelect={addModule}
        modules={modules}
      />

      <ModuleConfigDialog
        open={showModuleConfig}
        onClose={() => setShowModuleConfig(false)}
        onSave={() => loadModules()}
        currentConfig={moduleConfig}
      />

      <TerraformCommitDialog
        open={showCommitDialog}
        onClose={() => setShowCommitDialog(false)}
        hcl={mode === 'templates' ? templateHclPreview : combinedHCL}
      />

      <TerraformTemplateEditor
        open={showTemplateEditor}
        onClose={() => { setShowTemplateEditor(false); setEditingTemplate(null); }}
        onSave={() => { loadTemplates(); }}
        editTemplate={editingTemplate}
      />
    </Box>
  );
};
